import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { Button } from '@spacefill/uikit/src/components/Button';
import { Popover } from '@spacefill/uikit/src/components/Popover';

function groupByDay(conversations, t) {
    const startOfTodayDate = new Date();
    startOfTodayDate.setHours(0, 0, 0, 0);
    const startOfToday = startOfTodayDate.getTime();

    const startOfYesterdayDate = new Date(startOfTodayDate);
    startOfYesterdayDate.setDate(startOfYesterdayDate.getDate() - 1);
    const startOfYesterday = startOfYesterdayDate.getTime();

    const startOfSevenDaysAgoDate = new Date(startOfTodayDate);
    startOfSevenDaysAgoDate.setDate(startOfSevenDaysAgoDate.getDate() - 7);
    const startOfSevenDaysAgo = startOfSevenDaysAgoDate.getTime();

    const groups = { today: [], yesterday: [], week: [] };
    for (const conv of conversations) {
        const d = conv.updatedAt;
        if (d >= startOfToday) groups.today.push(conv);
        else if (d >= startOfYesterday) groups.yesterday.push(conv);
        else if (d >= startOfSevenDaysAgo) groups.week.push(conv);
    }

    return [
        { label: t('Today'), items: groups.today },
        { label: t('Yesterday'), items: groups.yesterday },
        { label: t('Last 7 days'), items: groups.week },
    ].filter((g) => g.items.length > 0);
}

export const AiConversationList = ({ conversations, activeId, onSelect, onNew, onDelete, t }) => {
    const groups = groupByDay(conversations, t);

    return (
        <div className='flex h-full flex-col overflow-hidden border-r border-slate-200 bg-gray-50'>
            <div className='flex shrink-0 items-center justify-between px-4 py-3 border-b border-slate-200'>
                <div>
                    <p className='text-[11px] font-bold tracking-[0.08em] text-gray-400 uppercase'>
                        {t('Conversations')}
                    </p>
                    <p className='text-[9px] uppercase text-gray-400'>
                        {t('Conversations are kept for last 7 days only')}
                    </p>
                </div>
            </div>

            <div className='flex-1 overflow-y-auto px-2 py-2'>
                {groups.length === 0 ? (
                    <p className='px-2 py-4 text-center text-xs text-gray-400'>{t('No conversations yet')}</p>
                ) : (
                    groups.map((group) => (
                        <div key={group.label}>
                            <p className='px-2 py-1.5 text-[10px] font-bold tracking-[0.08em] text-gray-400 uppercase'>
                                {group.label}
                            </p>
                            {group.items.map((conv) => (
                                <div
                                    key={conv.id}
                                    className={clsx(
                                        'group relative mb-1 cursor-pointer rounded-xl border px-2 py-1 transition-all',
                                        conv.id === activeId
                                            ? 'border-gray-200 bg-white'
                                            : 'border-transparent bg-transparent hover:border-gray-200 hover:bg-white'
                                    )}
                                    onClick={() => onSelect(conv)}
                                >
                                    <div className='flex items-start justify-between gap-2'>
                                        <div className='flex items-center gap-1.5 min-w-0'>
                                            <p className='truncate text-[13px] text-xs text-gray-800'>{conv.title}</p>
                                            <button
                                                type='button'
                                                onClick={(e) => e.stopPropagation()}
                                                className='opacity-0 group-hover:opacity-100'
                                            >
                                                <Popover
                                                    dataTestid='popover-delete-conversation'
                                                    position='auto'
                                                    content={({ close }) => (
                                                        <div className='text-sm'>
                                                            <p>
                                                                {t(
                                                                    'Are you sure you want to delete this conversation?'
                                                                )}
                                                            </p>
                                                            <div className='mt-4 flex justify-between gap-2'>
                                                                <Button
                                                                    dataTestid='confirm-abort-delete-conversation'
                                                                    variant='outlined'
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        close();
                                                                    }}
                                                                >
                                                                    {t('No')}
                                                                </Button>
                                                                <Button
                                                                    dataTestid='confirm-delete-conversation'
                                                                    variant='danger'
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onDelete(conv.id);
                                                                        close();
                                                                    }}
                                                                >
                                                                    {t('Yes, delete this conversation')}
                                                                </Button>
                                                            </div>
                                                        </div>
                                                    )}
                                                >
                                                    <span
                                                        className='w-5 p-2 flex items-center justify-center rounded-md text-gray-400 cursor-pointer hover:bg-red-50 hover:text-red-500'
                                                        title={t('Delete')}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTrashAlt}
                                                            className='text-[10px]'
                                                        />
                                                    </span>
                                                </Popover>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))
                )}
            </div>
            <div className='shrink-0 border-t border-slate-200 p-3 flex flex-col gap-2'>
                <button
                    type='button'
                    onClick={onNew}
                    className='flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-2.5 text-[13px] font-medium text-gray-700 transition-all hover:border-lime-300 hover:bg-lime-50/60'
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {t('New conversation')}
                </button>
            </div>
        </div>
    );
};
